/* Cyber Security Policy Cancellation */

import { unwrapResult } from "@reduxjs/toolkit";
import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import { createCaseComplaint } from "../../../../redux/features/crm/caseComplaintSlice";
import { resetPolicyCancellationFlow } from "../../../../redux/features/motorInsurancePolicyCancellationFlowSlice";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
  policyView,
} from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW,
} from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";
import CyberSecurityCancellationReasonPage from "./cyberSecurityCancellationReasonPage";
import CyberSecurityCancellationReviewPage from "./cyberSecurityCancellationReviewPage";
import CyberSecurityConfirmCancellationPage from "./cyberSecurityConfirmCancellationPage";

const CancelPolicyCyberSecurity = () => {
  const { currentStep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { policy_id } = useParams();

  const [selectedReasonOut, setSelectedReasonOut] = useState("");

  // Decode policy number from URL
  const policyNumber = decodeURIComponent(policy_id);

  useEffect(() => {
    dispatch(resetPolicyCancellationFlow());

    // Load cyber security policy data if not already loaded
    const polReq = {
      PolicyNumber: policyNumber,
      Channel: getClientSource(),
    };
    dispatch(policyView(polReq));
  }, [dispatch, policyNumber]);

  const handleNavigationToPolicyOverview = () => {
    // We need to recreate the encrypted policy ID to navigate back
    const secretKey = "i-hate-meej";
    const encryptedValue = CryptoJS.AES.encrypt(
      policyNumber,
      secretKey
    ).toString();

    // Convert to URL-safe format (same as policy card)
    const urlSafeEncryptedValue = encryptedValue
      .split("+")
      .join("-") // Replace '+' with '-'
      .split("/")
      .join("_") // Replace '/' with '_'
      .split("=")
      .join(""); // Remove '=' padding

    // Check if this is personal cyber or cyber security based on current URL
    const isPersonalCyber =
      window.location.pathname.includes("/personal-cyber/");
    const basePath = isPersonalCyber
      ? "/personal-cyber/"
      : "/cyber-security-insurance/";

    navigate(
      `${basePath}policy-overview/${urlSafeEncryptedValue}?tabType=ManagePolicy`
    );
  };

  // Get policy detail from Redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);
  const profileInfo = useSelector(selectProfileData);

  // Debug: Log all available data
  console.log("=== CYBER SECURITY POLICY DATA DEBUG ===");
  console.log("policyDetailsInfo:", policyDetailsInfo);
  console.log("policyDetailsPartyInfo:", policyDetailsPartyInfo);
  console.log("profileInfo:", profileInfo);
  console.log("policyNumber from URL:", policyNumber);
  console.log("=== END DEBUG ===");

  const confirmedCancellation = async () => {
    const payload = {
      FirstName:
        policyDetailsPartyInfo?.FirstName || profileInfo?.FirstName || "",
      LastName: policyDetailsPartyInfo?.LastName || profileInfo?.LastName || "",
      PhoneNumber:
        policyDetailsPartyInfo?.PhoneNumber || profileInfo?.PhoneNumber || "",
      Email: profileInfo?.Email || "",
      Subject: "Cancel Policy",
      Product: "Cyber Security", // Cyber Security insurance
      Lob: "LIABILITY", // Cyber Security → LIABILITY same as smart device
      Descreption: `Policy to be cancelled. \nSelected Reason: ${selectedReasonOut}`,
      ClaimNumber: "",
      PolicyNumber: policyNumber,
      VNumber: "", // Empty string for cyber security (not motor)
      Channel: getClientSource(),
    };

    console.log("=== CYBER SECURITY CANCEL API PAYLOAD ===");
    console.log(JSON.stringify(payload, null, 2));
    console.log("=== END PAYLOAD ===");

    // Calling API case/complaint
    try {
      const caseComplaintAction = await dispatch(createCaseComplaint(payload));
      const caseComplaintResult = unwrapResult(caseComplaintAction);

      if (caseComplaintResult) {
        handleNavigationToPolicyOverview();
        toast.success(
          translate(
            "Your request for cancellation has been successfully sent",
            "تم إرسال طلب الإلغاء بنجاح"
          )
        );
      }
    } catch (error) {
      toast.error(
        translate(
          "Error in sending cancel policy request",
          "خطأ في إرسال طلب إلغاء السياسة"
        )
      );
    }
  };

  const PageRenderer = () => {
    switch (currentStep?.name) {
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW:
        return (
          <CyberSecurityCancellationReviewPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON:
        return (
          <CyberSecurityCancellationReasonPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            setSelectedReasonOut={setSelectedReasonOut}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION:
        return (
          <CyberSecurityConfirmCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            confirmedCancellation={confirmedCancellation}
            selectedReasonOut={selectedReasonOut}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[7%] ipad:h-[93vh] ipad:overflow-y-scroll">
      {PageRenderer()}
    </div>
  );
};

export default CancelPolicyCyberSecurity;
